import { Alert, Box, Button, Radio, Typography } from "@mui/material";
import { WarningAmber } from "@mui/icons-material";
import { useState } from "react";
import GenericModal from "../../Modal/GenericModal";
import { Vocabulary } from "../../Utils/Vocabulary";

export type MonthYear = { month: number; year: number };

type TrimesterSyncOption = "current_month" | "current_and_past";

type SyncOptionConfig = {
  value: TrimesterSyncOption;
  label: string;
  description: string;
  months: MonthYear[];
  warning?: string;
};

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatMonthYear(date: Date): string {
  return date.toLocaleString("ro-RO", { month: "long", year: "numeric" });
}

function buildSyncOptions(): SyncOptionConfig[] {
  const now = new Date();
  const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const currentMonthYear: MonthYear = { month: now.getMonth() + 1, year: now.getFullYear() };
  const prevMonthYear: MonthYear = { month: prevMonthDate.getMonth() + 1, year: prevMonthDate.getFullYear() };

  const currentLabel = capitalize(formatMonthYear(now));
  const prevLabel = formatMonthYear(prevMonthDate);

  return [
    {
      value: "current_month",
      label: currentLabel,
      description: Vocabulary.syncCurrentMonthOnlyDescription,
      months: [currentMonthYear],
    },
    {
      value: "current_and_past",
      label: `${currentLabel} și ${prevLabel}`,
      description: Vocabulary.syncCurrentAndPastMonthsDescription,
      months: [currentMonthYear, prevMonthYear],
      warning: Vocabulary.locationChangedBonusSyncWarningPast,
    },
  ];
}

type TrimesterSyncDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (months: MonthYear[]) => void;
  title?: string;
  description?: string;
  confirmLabel?: string;
  optionDescriptions?: Partial<Record<TrimesterSyncOption, string>>;
  optionWarnings?: Partial<Record<TrimesterSyncOption, string>>;
};

export default function TrimesterSyncDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  optionDescriptions,
  optionWarnings,
}: TrimesterSyncDialogProps) {
  const [selected, setSelected] = useState<TrimesterSyncOption>("current_month");
  const syncOptions = buildSyncOptions().map((opt) => ({
    ...opt,
    ...(optionDescriptions?.[opt.value] && { description: optionDescriptions[opt.value] as string }),
    ...(optionWarnings?.[opt.value] !== undefined && { warning: optionWarnings[opt.value] }),
  }));

  function handleConfirm() {
    const months = syncOptions.find((o) => o.value === selected)?.months ?? [];
    onConfirm(months);
    setSelected("current_month");
  }

  function handleClose() {
    setSelected("current_month");
    onClose();
  }

  return (
    <GenericModal
      open={open}
      onClose={handleClose}
      title={title ?? Vocabulary.locationChangedBonusSyncTitle}
      maxWidth={520}
      zIndex={1400}
      buttons={
        <Box sx={{ display: "flex", gap: 2, width: "100%", px: 1, pb: 1 }}>
          <Button fullWidth variant="contained" color="secondary" onClick={handleClose}>
            {Vocabulary.cancel}
          </Button>
          <Button fullWidth variant="contained" style={{ color: "white" }} onClick={handleConfirm}>
            {confirmLabel ?? Vocabulary.syncBonuses}
          </Button>
        </Box>
      }
    >
      <Box sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description ?? Vocabulary.locationChangedBonusSyncDescription}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {syncOptions.map((opt) => {
            const isSelected = selected === opt.value;
            return (
              <Box
                key={opt.value}
                onClick={() => setSelected(opt.value)}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  padding: "12px 14px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  border: isSelected ? "1.5px solid #1976d2" : "1px solid rgba(0,0,0,0.12)",
                  background: isSelected ? "rgba(25, 118, 210, 0.06)" : "rgba(0,0,0,0.01)",
                  transition: "all 0.2s ease",
                }}
              >
                <Radio
                  checked={isSelected}
                  size="small"
                  sx={{ mt: "-2px", p: 0 }}
                  onChange={() => setSelected(opt.value)}
                />
                <Box>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: isSelected ? 600 : 400 }}>
                    {opt.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                    {opt.description}
                  </Typography>
                  {isSelected && opt.warning && (
                    <Alert
                      severity="warning"
                      icon={<WarningAmber fontSize="inherit" />}
                      sx={{
                        mt: 1,
                        borderRadius: "6px",
                        py: 0.5,
                        boxSizing: "border-box",
                        width: "100%",
                        "& .MuiAlert-message": { overflow: "hidden", wordBreak: "break-word" },
                      }}
                    >
                      {opt.warning}
                    </Alert>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </GenericModal>
  );
}
